import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Plus } from 'lucide-react';
import CreateCampaignModal from '@/components/Modals/CreateCompaignModal';
import CampaignDetailsModal from '@/components/Modals/CampaignDetailsModal';
import EditCampaignModal from '@/components/Modals/EditCampaignModal';
import CreateTemplateModal from '@/components/Modals/CreateTemplateModal';
import api from '@/lib/api';

const CampaignPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [campaignToEdit, setCampaignToEdit] = useState(null);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    scenario: '',
    emailCount: 3,
    frequency: 3,
    stagger: 'no',
    selectedContacts: [],
    templateId: null,
    status: 'draft',
  });
  const [templates, setTemplates] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({});
  const [scenarios, setScenarios] = useState([]);
  
    // map value -> label
    const scenarioLabelMap = useMemo(() => {
      const map = {};
      (scenarios || []).forEach(s => { map[String(s.value)] = s.label; });
      return map;
    }, [scenarios]);
  
  const getScenarioLabel = useCallback(
    (val) => scenarioLabelMap[String(val)] || String(val || ''),
    [scenarioLabelMap]
  );
  useEffect(() => {
    api.get('/api/templates/')
      .then(res => setTemplates(res.data?.results || []))
      .catch(err => console.error('Ошибка загрузки шаблонов:', err));

    api.get('/api/contacts/organizations-with-contacts/')
      .then(res => {
        console.log('📦 Organizations response:', res.data);
        setOrganizations(res.data);
      })
      .catch(err => console.error('Ошибка загрузки организаций:', err));

    api.get('/api/campaigns/scenarios/')
      .then(res => setScenarios(res.data || []))
      .catch(err => console.error('Ошибка загрузки сценариев:', err));
  }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await api.get('/api/campaigns/all/');
      setCampaigns(res.data?.results || []);
    } catch (err) {
      console.error('❌ Ошибка загрузки кампаний:', err);
      alert('Не удалось загрузить кампании.');
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await api.delete(`/api/campaigns/${id}/delete/`);
      setCampaigns(prev => prev.filter(c => c.id !== id));
      setShowDetailsModal(false);
    } catch (err) {
      console.error('❌ Ошибка удаления кампании:', err);
      alert('Не удалось удалить кампанию.');
    }
  };

  const handleCreateTemplate = (createdTemplate) => {
    setNewCampaign((prev) => ({
      ...prev,
      templateId: createdTemplate.id,
    }));
    setShowCreateTemplateModal(false);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold flex items-center justify-between">
        Campaigns
        <Button onClick={() => setShowCreateCampaignModal(true)}>
          <Plus className="mr-2 h-4 w-4" /> Create New Campaign
        </Button>
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.isArray(campaigns) ? (
          campaigns.length === 0 ? (
            <p className="text-center text-muted-foreground col-span-full">
              No campaigns created yet. Click "Create New Campaign" to get started.
            </p>
          ) : (
            campaigns.map((campaign) => (
              <Card
                key={campaign.id}
                className="hover-lift cursor-pointer"
                onClick={() => {
                  setSelectedCampaign(campaign);
                  setShowDetailsModal(true);
                }}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    {campaign.name}
                    <Badge>{campaign.status || 'Draft'}</Badge>
                  </CardTitle>
                  <CardDescription>{getScenarioLabel(campaign.scenario)}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Total Contacts:</span>
                    <span className="font-medium">{campaign.total_contacts ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Replies:</span>
                    <span className="font-medium text-green-600">{campaign.replied ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Opened:</span>
                    <span className="font-medium text-blue-600">{campaign.opened ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>No Response:</span>
                    <span className="font-medium text-orange-600">{campaign.no_response ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Progress:</span>
                    <span className="font-medium">{campaign.progress_percent}%</span>
                  </div>
                  <Progress value={campaign.progress_percent} className="w-full" />
                </CardContent>
              </Card>
            ))
          )
        ) : (
          <p className="text-center text-red-500 col-span-full">
            ⚠ Ошибка: список кампаний не загружен или не является массивом.
          </p>
        )}
      </div>

      <CreateCampaignModal
        isOpen={showCreateCampaignModal}
        onClose={() => setShowCreateCampaignModal(false)}
        newCampaign={newCampaign}
        setNewCampaign={setNewCampaign}
        templates={templates}
        organizations={organizations}
        getTemplateById={() => null}
        onCreateCampaign={fetchCampaigns}
        showCreateTemplateModal={showCreateTemplateModal}
        setShowCreateTemplateModal={setShowCreateTemplateModal}
        newTemplate={newTemplate}
        setNewTemplate={setNewTemplate}
        handleCreateTemplate={handleCreateTemplate}
      />
      {campaignToEdit && (
        <EditCampaignModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setCampaignToEdit(null);
          }}
          campaignId={campaignToEdit?.id}
          templates={templates}
          organizations={organizations}
          onSave={fetchCampaigns}
        />
      )}

      <CampaignDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        campaignId={selectedCampaign?.id}
        scenarios={scenarios}
        onEdit={(campaign) => {
          setCampaignToEdit(campaign);
          setShowDetailsModal(false);
          setShowEditModal(true);
        }}
        onDelete={handleDeleteCampaign}
      />
    </div>
  );
};

export default CampaignPage;